/**
 * Display values for the Timesheet Approvals workspace.
 *
 * Every coded value the Approvals API returns (a day's status, how it was approved, how a
 * Timesheet differs from what capture produced, what kind of reminder was sent) is mapped here
 * to a label and a semantic tone. Templates call the `resolve*` functions and never branch on a
 * code (see CLAUDE.md, "Keep logic out of templates").
 *
 * Each entry is `{ Label, Tone, ... }`. Tone is a semantic bucket the component maps to a CSS
 * class, never a raw colour. Where a map says it feeds the design system's status chip, its
 * Tone is one of the `STATUS` keys in `components2/status/statusMeta.js`.
 *
 * An unmapped code degrades to the raw code with a neutral tone (the shared rule in
 * `helpers/display-values.js`), so a new API value shows up visibly instead of rendering blank.
 * Adding a newly supported code is one line in the relevant map.
 */

import { resolveDisplayValue } from "/helpers/display-values.js";
import { STATUS } from "/components2/index.js";
import {
	formatPendingBadgeLabel, formatMyOutstandingBadgeLabel, formatPendingBadgeTooltip, formatMyOutstandingBadgeTooltip,
} from "./approvals-formatting.js";




//-- A step in a day's review history (ApprovalsDay.ReviewHistory[].Action)

/**
 * The wording and tone of each step. Tone is the timeline dot: a send-back is danger, a resubmission or a re-open needs
 * the approver's attention (pending), an approval is success, the rest is neutral.
 */
export const reviewEventActions = {
	Submitted: { Label: "Submitted", Tone: "neutral" },
	Recalled: { Label: "Recalled to draft", Tone: "neutral" },
	Approved: { Label: "Approved", Tone: "success" },
	Rejected: { Label: "Sent back (rejected)", Tone: "danger" },
	Resubmitted: { Label: "Re-submitted", Tone: "pending" },
	Reopened: { Label: "Re-opened after approval", Tone: "pending" },
	AutoApproved: { Label: "Approved automatically", Tone: "success" },
};


export function resolveReviewEventAction(code)
{
	return resolveDisplayValue(reviewEventActions, code, "neutral");
}



//-- A day's status in the workspace (ApprovalsDay.StatusCode)

/**
 * `NotSubmitted` is a day the person has not handed over (no Timesheet content is sent for it);
 * `Blank` is a day with nothing to show (an unworked weekend day). Tone is a `STATUS` key.
 */
export const approvalDayStatuses = {
	NotSubmitted: { Label: "Not submitted", Tone: "unconfirmed" },
	Pending: { Label: "Pending", Tone: "pending" },
	Approved: { Label: "Approved", Tone: "approved" },
	Rejected: { Label: "Rejected", Tone: "rejected" },
	Blank: { Label: "No time", Tone: "notreq" },
};


export function resolveApprovalDayStatus(code)
{
	return resolveDisplayValue(approvalDayStatuses, code, "neutral");
}


/**
 * The `LStatusChip` `state` for a day status. The chip only knows the six lifecycle states, so a
 * code with no mapping (a neutral tone) shows as the muted "not required" shape rather than
 * borrowing a real status's colour.
 */
export function approvalDayChipState(code)
{
	const tone = resolveApprovalDayStatus(code).Tone;

	return Object.prototype.hasOwnProperty.call(STATUS, tone) ? tone : "notreq";
}


/**
 * The filter chips above the Overview matrix, in order. `Code` is the `?status=` query value and
 * the key on `Counts` (null for All, which has no count of its own).
 */
export const approvalStatusFilters = [
	{ Code: "all", Label: "All", CountKey: null },
	{ Code: "Pending", Label: "Pending", CountKey: "Pending" },
	{ Code: "Approved", Label: "Approved", CountKey: "Approved" },
	{ Code: "Rejected", Label: "Rejected", CountKey: "Rejected" },
	{ Code: "NotSubmitted", Label: "Not submitted", CountKey: "NotSubmitted" },
];




//-- How a day was approved (ApprovalsReview.Manner)

/**
 * The suffix after "Approved": nothing for an individual approval, " · bulk" for a bulk one and
 * " · automatic" for one made by the system when approvals are switched off.
 */
export const approvalManners = {
	Individual: { Label: "", Tone: "neutral" },
	Bulk: { Label: " · bulk", Tone: "neutral" },
	Auto: { Label: " · automatic", Tone: "neutral" },
};


export function resolveApprovalManner(code)
{
	if (!code) return approvalManners.Individual;

	const entry = approvalManners[code];
	if (entry) return entry;

	// Unknown manner: show it, visibly, rather than dropping it.
	return { Label: " · " + code, Tone: "neutral" };
}




//-- How a Timesheet entry differs from what capture produced (ApprovalsDayVariance.Mechanism)

/**
 * `Label` is the mechanism's name; `ChipLabel` is the tail of the "Differs from what was captured"
 * toggle on an entry. A change an owner made reads as pending (attention); anything the capture
 * engine did itself is neutral.
 */
export const varianceMechanisms = {
	Captured: { Label: "Captured", Tone: "neutral", ChipLabel: "" },
	Reallocated: { Label: "Reallocated", Tone: "pending", ChipLabel: "review change" },
	Split: { Label: "Split & reallocated", Tone: "pending", ChipLabel: "review reallocation" },
	Merged: { Label: "Merged", Tone: "pending", ChipLabel: "review change" },
	AddedManually: { Label: "Added manually", Tone: "pending", ChipLabel: "review change" },
	Deleted: { Label: "Deleted", Tone: "pending", ChipLabel: "review change" },
	DurationChanged: { Label: "Duration changed", Tone: "pending", ChipLabel: "review change" },
	BillingChanged: { Label: "Billing changed", Tone: "pending", ChipLabel: "review change" },
	TaskChanged: { Label: "Task changed", Tone: "pending", ChipLabel: "review change" },
	WaywoApplied: { Label: "Set from a WAYWO answer", Tone: "neutral", ChipLabel: "" },
	EngineDissolved: { Label: "Folded into a neighbour by capture", Tone: "neutral", ChipLabel: "" },
	EngineRepaired: { Label: "Trimmed by capture to fix an overlap", Tone: "neutral", ChipLabel: "" },
};


export function resolveVarianceMechanism(code)
{
	return resolveDisplayValue(varianceMechanisms, code, "neutral");
}


/**
 * The tail of the "Differs from what was captured — …" toggle. An unmapped mechanism still
 * says "review change", because a difference is a difference.
 */
export function resolveVarianceChipLabel(code)
{
	const entry = varianceMechanisms[code];

	return entry && entry.ChipLabel ? entry.ChipLabel : "review change";
}




//-- Reminders (ApprovalsReminderItem.Type)

export const reminderTypes = {
	Automated: { Label: "automated reminder", Tone: "neutral" },
	Manual: { Label: "manual nudge", Tone: "pending" },
};


export function resolveReminderType(code)
{
	return resolveDisplayValue(reminderTypes, code, "neutral");
}




//-- Why the workspace can't be shown (approvals-store.js getApprovalsAccessState())

export const approvalsAccessStates = {
	loading: { Label: "Loading…", Tone: "neutral", Reason: "" },
	off: { Label: "Approvals aren't turned on for this account", Tone: "neutral", Reason: "An account administrator can turn Timesheet approvals on in Organisation Settings." },
	notApprover: { Label: "You don't have approval access", Tone: "neutral", Reason: "Ask an account administrator if you should be approving your team's time." },
	noTeam: { Label: "No team is mapped to you as approver", Tone: "neutral", Reason: "Ask an account administrator to make you the approver of a team." },
};


export function resolveApprovalsAccessState(code)
{
	return resolveDisplayValue(approvalsAccessStates, code, "neutral");
}




//-- Navigation badges (`badgeKey` on a navigation item)

/**
 * The count badges in the side navigation. A nav item names one with `badgeKey`; the layout
 * looks it up here for its tone and accessible text, and in `approvals-store.js` for its count.
 * `Describe(count, windowWeeks)` is the text a screen reader hears; `Explain(count, windowWeeks)` is the hover text,
 * which says what the number counts and over what period (`windowWeeks` is the configured window length).
 *
 * Approvals is `pending` (an approver has something to do); Home is `neutral`, because it is a
 * person's own backlog rather than something waiting on them to act at once.
 */
export const navBadges = {
	approvalsPending: { Tone: "pending", Describe: formatPendingBadgeLabel, Explain: formatPendingBadgeTooltip },
	myOutstanding: { Tone: "neutral", Describe: formatMyOutstandingBadgeLabel, Explain: formatMyOutstandingBadgeTooltip },
};


export function resolveNavBadge(key)
{
	return navBadges[key] || { Tone: "neutral", Describe: (count) => String(count), Explain: () => "" };
}




//-- The outcome of one item in a bulk or single action

/**
 * What `ApproveTimesheets` (and `RejectTimesheet`, which adds `Rejected`) report for one item.
 * `Tone` is `success` (it happened), `pending` (needs the user's attention: the day changed, was
 * no longer pending, or was being updated) or `danger` (refused, or not there).
 */
export const approveOutcomes = {
	Approved: { Label: "Approved", Tone: "success", Message: "Approved" },
	Rejected: { Label: "Sent back", Tone: "success", Message: "Sent back" },
	Changed: { Label: "Changed since you opened it", Tone: "pending", Message: "This day changed since you opened it — reloaded" },
	NotPending: { Label: "No longer pending", Tone: "pending", Message: "This day is no longer pending — reloaded" },
	Busy: { Label: "Being updated, try again", Tone: "pending", Message: "This day is being updated — try again in a moment" },
	NotAuthorised: { Label: "Not authorised", Tone: "danger", Message: "You can't act on this day" },
	NotFound: { Label: "Not found", Tone: "danger", Message: "This day couldn't be found" },
};


/**
 * The toast type (`shared.js` showToast: success | info | warning | error) for an outcome's tone.
 */
export const toastTypesByTone = {
	success: "success",
	pending: "warning",
	danger: "error",
	neutral: "info",
};


export function resolveToastType(tone)
{
	return toastTypesByTone[tone] || "info";
}


export function resolveApproveOutcome(code)
{
	return resolveDisplayValue(approveOutcomes, code, "neutral");
}


export const reminderOutcomes = {
	Sent: { Label: "Sent", Tone: "success" },
	NothingOutstanding: { Label: "Nothing outstanding", Tone: "neutral" },
	NotAuthorised: { Label: "Not authorised", Tone: "danger" },
	Failed: { Label: "Failed", Tone: "danger" },
};


export function resolveReminderOutcome(code)
{
	return resolveDisplayValue(reminderOutcomes, code, "neutral");
}
